import React from 'react';
import {
  Alert,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Collapse,
  Card,
} from 'react-bootstrap';
import type {JobDescriptionSchema} from './job';
import type {JobDescriptionDbModel} from './database-models';

declare function structuredClone<T>(value: T): T;

const userIdKey = 'user_id';

type Props = {
  value?: JobDescriptionDbModel;
  onChange?: (v: JobDescriptionDbModel) => void;
  onSubmit?: (v: JobDescriptionDbModel) => void;
  readOnly?: boolean;
  showTopActions?: boolean;
};

function safeArray<T>(v: T[] | undefined): T[] {
  return Array.isArray(v) ? v : [];
}

function reorder<T>(array: T[], from: number, to: number): T[] {
  const next = [...array];
  if (to < 0 || to >= next.length || from === to) {
    return next;
  }

  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function deepClone<T>(v: T): T {
  return structuredClone(v);
}

const iso8601Pattern =
  /^([12]\d{3}-[01]\d-[0-3]\d|[12]\d{3}-[01]\d|[12]\d{3})$/;

type JobFormValidationErrors = {
  name?: string;
  title?: string;
  company?: string;
  type?: string;
  date?: string;
  description?: string;
  location?: string;
  remote?: string;
  salary?: string;
  experience?: string;
  responsibilities?: string;
  qualifications?: string;
  skills?: string;
  summary: string[];
};

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function addError(
  errors: JobFormValidationErrors,
  key: keyof Omit<JobFormValidationErrors, 'summary'>,
  message: string,
): void {
  errors[key] ??= message;

  errors.summary.push(message);
}

function validateStringField(
  errors: JobFormValidationErrors,
  key: keyof Omit<JobFormValidationErrors, 'summary'>,
  value: unknown,
  message: string,
): void {
  if (value !== undefined && !isString(value)) {
    addError(errors, key, message);
  }
}

function validateStringArrayField(
  errors: JobFormValidationErrors,
  key: keyof Omit<JobFormValidationErrors, 'summary'>,
  value: unknown,
  message: string,
): void {
  if (value === undefined) {
    return;
  }

  if (!Array.isArray(value) || value.some(item => !isString(item))) {
    addError(errors, key, message);
  }
}

function isValidLocation(value: unknown): boolean {
  if (!isRecord(value) || Array.isArray(value)) {
    return false;
  }

  return [
    ['address', 'Address must be a string.'],
    ['postalCode', 'Postal code must be a string.'],
    ['city', 'City must be a string.'],
    ['countryCode', 'Country code must be a string.'],
    ['region', 'Region must be a string.'],
  ].every(
    ([field]) =>
      !(
        field in value &&
        value[field] !== undefined &&
        !isString(value[field])
      ),
  );
}

function isValidSkill(value: unknown): boolean {
  if (!isRecord(value) || Array.isArray(value)) {
    return false;
  }

  const fieldsAreValid = [['name'], ['level']].every(
    ([field]) =>
      !(
        field in value &&
        value[field] !== undefined &&
        !isString(value[field])
      ),
  );
  if (!fieldsAreValid) {
    return false;
  }

  if (!('keywords' in value) || value.keywords === undefined) {
    return true;
  }

  return (
    Array.isArray(value.keywords) &&
    value.keywords.every(item => isString(item))
  );
}

function validateJobPayload(
  payload: JobDescriptionDbModel,
): JobFormValidationErrors {
  const errors: JobFormValidationErrors = {summary: []};
  const job = payload.job ?? {};

  if (!isString(payload.name)) {
    addError(errors, 'name', 'Name must be a string.');
  }

  validateStringField(errors, 'title', job.title, 'Title must be a string.');
  validateStringField(
    errors,
    'company',
    job.company,
    'Company must be a string.',
  );
  validateStringField(errors, 'type', job.type, 'Type must be a string.');

  if (
    job.date !== undefined &&
    (!isString(job.date) || !iso8601Pattern.test(job.date))
  ) {
    addError(errors, 'date', 'Date must use YYYY, YYYY-MM, or YYYY-MM-DD.');
  }

  validateStringField(
    errors,
    'description',
    job.description,
    'Description must be a string.',
  );

  if (
    job.remote !== undefined &&
    (!isString(job.remote) || !['Full', 'Hybrid', 'None'].includes(job.remote))
  ) {
    addError(errors, 'remote', 'Remote must be Full, Hybrid, or None.');
  }

  validateStringField(errors, 'salary', job.salary, 'Salary must be a string.');
  validateStringField(
    errors,
    'experience',
    job.experience,
    'Experience must be a string.',
  );

  if (job.location !== undefined && !isValidLocation(job.location)) {
    addError(errors, 'location', 'Location must contain only string fields.');
  }

  validateStringArrayField(
    errors,
    'responsibilities',
    job.responsibilities,
    'Responsibilities must be an array of strings.',
  );
  validateStringArrayField(
    errors,
    'qualifications',
    job.qualifications,
    'Qualifications must be an array of strings.',
  );

  if (
    job.skills !== undefined &&
    (!Array.isArray(job.skills) ||
      !job.skills.every(skill => isValidSkill(skill)))
  ) {
    addError(
      errors,
      'skills',
      'Each skill must be an object with string fields and string keywords.',
    );
  }

  return errors;
}

function makeDbModel(
  base: JobDescriptionDbModel | undefined,
  patch: Partial<JobDescriptionDbModel>,
): JobDescriptionDbModel {
  return {
    ...base,
    ...patch,
    [userIdKey]: base?.user_id ?? '',
    id: base?.id ?? '',
    name: patch.name ?? base?.name ?? '',
    url: patch.url ?? base?.url,
    job: patch.job ?? base?.job ?? {},
  };
}

export default function JobForm({
  value,
  onChange,
  onSubmit,
  readOnly,
  showTopActions,
}: Props): React.JSX.Element {
  const [form, setForm] = React.useState<JobDescriptionSchema>(
    value?.job ?? {},
  );

  React.useEffect(() => {
    setForm(value?.job ?? {});
  }, [value]);

  const [locOpen, setLocOpen] = React.useState<boolean>(true);
  const [respOpen, setRespOpen] = React.useState<boolean>(true);
  const [qualOpen, setQualOpen] = React.useState<boolean>(true);
  const [skillsOpen, setSkillsOpen] = React.useState<boolean>(true);
  const [descOpen, setDescOpen] = React.useState<boolean>(true);
  const [basicsOpen, setBasicsOpen] = React.useState<boolean>(true);

  const [keywordDrafts, setKeywordDrafts] = React.useState<
    Record<number, string>
  >({});

  // Keep a per-skill draft string so we don't reparse/sync the keywords on every keystroke.
  // This preserves caret position and allows normal editing/backspacing.
  React.useEffect(() => {
    const drafts: Record<number, string> = {};
    const skills = safeArray(form.skills);
    let i = 0;
    for (const s of skills) {
      drafts[i] = (s?.keywords ?? []).join(', ');
      i += 1;
    }

    setKeywordDrafts(drafts);
  }, [form.skills]);

  const [name, setName] = React.useState<string>(value?.name ?? '');
  const [url, setUrl] = React.useState<string | undefined>(value?.url);

  React.useEffect(() => {
    setName(value?.name ?? '');
    setUrl(value?.url);
  }, [value]);

  // Track a baseline snapshot (initial) to enable true Reset behavior
  const baselineRef = React.useRef<JobDescriptionDbModel | undefined>(
    value ? deepClone(value) : undefined,
  );
  React.useEffect(() => {
    if (value && value.id !== baselineRef.current?.id) {
      baselineRef.current = deepClone(value);
    }
  }, [value]);

  const [resetNonce, setResetNonce] = React.useState(0);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const validationErrors = React.useMemo(
    () => validateJobPayload(makeDbModel(value, {name, url, job: form})),
    [form, name, url, value],
  );

  const emitChange = (next: JobDescriptionSchema) => {
    setForm(next);
    onChange?.(makeDbModel(value, {name, url, job: next}));
  };

  const updateField = <K extends keyof JobDescriptionSchema>(
    key: K,
    v: JobDescriptionSchema[K],
  ) => {
    emitChange({...form, [key]: v});
  };

  const updateLocation = (patch: Partial<JobDescriptionSchema['location']>) => {
    emitChange({...form, location: {...form.location, ...patch}});
  };

  const updateSkill = (
    idx: number,
    patch: Partial<NonNullable<JobDescriptionSchema['skills']>[number]>,
  ) => {
    const skills = safeArray(form.skills).map((s, i) =>
      i === idx ? {...s, ...patch} : s,
    );
    emitChange({...form, skills});
  };

  const addSkill = () => {
    emitChange({
      ...form,
      skills: [...safeArray(form.skills), {name: '', level: '', keywords: []}],
    });
  };

  const removeSkill = (idx: number) => {
    const skills = safeArray(form.skills).filter((_, i) => i !== idx);
    emitChange({...form, skills});
  };

  const addStringArrayItem = (key: 'responsibilities' | 'qualifications') => {
    const array = safeArray(form[key]);
    emitChange({...form, [key]: [...array, '']});
  };

  const updateStringArrayItem = (
    key: 'responsibilities' | 'qualifications',
    idx: number,
    value: string,
  ) => {
    const array = safeArray(form[key]).map((it, i) => (i === idx ? value : it));
    emitChange({...form, [key]: array});
  };

  const removeStringArrayItem = (
    key: 'responsibilities' | 'qualifications',
    idx: number,
  ) => {
    const array = safeArray(form[key]).filter((_, i) => i !== idx);
    emitChange({...form, [key]: array});
  };

  const moveSkill = (from: number, dir: 'up' | 'down') => {
    const to = dir === 'up' ? from - 1 : from + 1;
    const skills = reorder(safeArray(form.skills), from, to);
    emitChange({...form, skills});
  };

  const moveStringArrayItem = (
    key: 'responsibilities' | 'qualifications',
    from: number,
    dir: 'up' | 'down',
  ) => {
    const to = dir === 'up' ? from - 1 : from + 1;
    const array = reorder(safeArray(form[key]), from, to);
    emitChange({...form, [key]: array});
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitAttempted(true);

    const next = makeDbModel(value, {name, url, job: form});
    const errors = validateJobPayload(next);
    if (errors.summary.length > 0) {
      return;
    }

    onSubmit?.(next);
  };

  const handleReset = () => {
    // Reset strictly to baseline (initial) value; if none, clear the form
    const base = baselineRef.current;
    setSubmitAttempted(false);
    if (!base) {
      setForm({});
      setName('');
      setUrl(undefined);
      onChange?.(makeDbModel(undefined, {name: '', url: undefined, job: {}}));
      setResetNonce(n => n + 1);
      return;
    }

    const nm = base.name ?? '';
    const u = base.url;
    setForm(deepClone(base.job ?? {}));
    setName(nm);
    setUrl(u);
    onChange?.(
      makeDbModel(base, {
        name: nm,
        url: u,
        job: deepClone(base.job ?? {}),
      }),
    );
    setResetNonce(n => n + 1);
  };

  const actionButtons = readOnly ? null : (
    <>
      <Button type='submit' className='me-2'>
        Save
      </Button>
      <Button variant='outline-secondary' onClick={handleReset}>
        Reset
      </Button>
    </>
  );

  return (
    <Form key={resetNonce} onSubmit={handleSubmit}>
      {showTopActions && !readOnly && (
        <div className='d-flex justify-content-end mb-3'>{actionButtons}</div>
      )}

      {submitAttempted && validationErrors.summary.length > 0 && (
        <Alert variant='danger' className='mb-3'>
          <div className='fw-semibold mb-1'>Fix these job format issues:</div>
          <ul className='mb-0 ps-3'>
            {validationErrors.summary.map((message, idx) => (
              <li key={`${message}-${idx}`}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Form.Group className='mb-3' controlId='jobName'>
        <Form.Label>Name</Form.Label>
        <Form.Control
          type='text'
          value={name}
          onChange={event => {
            const newName = event.currentTarget.value;
            setName(newName);
            onChange?.(
              makeDbModel(value, {
                name: newName,
                url,
                job: value?.job ?? form,
              }),
            );
          }}
          readOnly={readOnly}
          placeholder='Name used to identify this job'
          isInvalid={submitAttempted && Boolean(validationErrors.name)}
        />
        <Form.Control.Feedback type='invalid'>
          {validationErrors.name}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className='mb-3' controlId='jobUrl'>
        <Form.Label>URL</Form.Label>
        <Form.Control
          type='text'
          value={url ?? ''}
          onChange={event => {
            const newUrl = event.currentTarget.value;
            setUrl(newUrl);
            onChange?.(
              makeDbModel(value, {
                name,
                url: newUrl,
                job: value?.job ?? form,
              }),
            );
          }}
          readOnly={readOnly}
          placeholder='Optional URL associated with this job'
        />
      </Form.Group>
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setBasicsOpen(!basicsOpen);
            }}
            aria-controls='basics-collapse'
            aria-expanded={basicsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{basicsOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Basics</Card.Title>
        </div>

        <Collapse in={basicsOpen}>
          <div id='basics-collapse'>
            <Card.Body>
              <Row className='mt-2 mb-3'>
                <Form.Group as={Col} controlId='jobTitle'>
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.title ?? ''}
                    onChange={event => {
                      updateField('title', event.target.value);
                    }}
                    readOnly={readOnly}
                    placeholder='e.g. Web Developer'
                    isInvalid={
                      submitAttempted && Boolean(validationErrors.title)
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    {validationErrors.title}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} controlId='jobCompany'>
                  <Form.Label>Company</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.company ?? ''}
                    onChange={event => {
                      updateField('company', event.target.value);
                    }}
                    readOnly={readOnly}
                    placeholder='e.g. Acme Corp'
                    isInvalid={
                      submitAttempted && Boolean(validationErrors.company)
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    {validationErrors.company}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className='mb-3'>
                <Form.Group as={Col} controlId='jobType'>
                  <Form.Label>Type</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.type ?? ''}
                    onChange={event => {
                      updateField('type', event.target.value);
                    }}
                    readOnly={readOnly}
                    placeholder='Full-time, Part-time, Contract...'
                    isInvalid={
                      submitAttempted && Boolean(validationErrors.type)
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    {validationErrors.type}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} controlId='jobDate'>
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.date ?? ''}
                    onChange={event => {
                      updateField('date', event.target.value);
                    }}
                    readOnly={readOnly}
                    placeholder='ISO8601 (e.g. 2023-04 or 2014-06-29)'
                    isInvalid={
                      submitAttempted && Boolean(validationErrors.date)
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    {validationErrors.date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className='mb-3'>
                <Form.Group as={Col} controlId='jobRemote'>
                  <Form.Label>Remote</Form.Label>
                  <Form.Select
                    value={form.remote ?? ''}
                    onChange={event => {
                      const remote = event.currentTarget.value;
                      updateField(
                        'remote',
                        remote === ''
                          ? undefined
                          : remote === 'Full' ||
                              remote === 'Hybrid' ||
                              remote === 'None'
                            ? remote
                            : undefined,
                      );
                    }}
                    disabled={readOnly}
                    isInvalid={
                      submitAttempted && Boolean(validationErrors.remote)
                    }
                  >
                    <option value=''>(unspecified)</option>
                    <option value='Full'>Full</option>
                    <option value='Hybrid'>Hybrid</option>
                    <option value='None'>None</option>
                  </Form.Select>
                  {submitAttempted && validationErrors.remote && (
                    <div className='text-danger small mt-1'>
                      {validationErrors.remote}
                    </div>
                  )}
                </Form.Group>

                <Form.Group as={Col} controlId='jobSalary'>
                  <Form.Label>Salary</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type='text'
                      value={form.salary ?? ''}
                      onChange={event => {
                        updateField('salary', event.target.value);
                      }}
                      readOnly={readOnly}
                      placeholder='e.g. 100000'
                      isInvalid={
                        submitAttempted && Boolean(validationErrors.salary)
                      }
                    />
                    <Form.Control.Feedback type='invalid'>
                      {validationErrors.salary}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                <Form.Group as={Col} controlId='jobExperience'>
                  <Form.Label>Experience</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.experience ?? ''}
                    onChange={event => {
                      updateField('experience', event.target.value);
                    }}
                    readOnly={readOnly}
                    placeholder='e.g. Senior, Mid-level, Junior'
                    isInvalid={
                      submitAttempted && Boolean(validationErrors.experience)
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    {validationErrors.experience}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setDescOpen(!descOpen);
            }}
            aria-controls='description-collapse'
            aria-expanded={descOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{descOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Description</Card.Title>
        </div>
        <Collapse in={descOpen}>
          <div id='description-collapse'>
            <Card.Body>
              {submitAttempted && validationErrors.description && (
                <Alert variant='danger' className='py-2'>
                  {validationErrors.description}
                </Alert>
              )}
              <Form.Group className='mb-3' controlId='jobDescription'>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={4}
                  value={form.description ?? ''}
                  onChange={event => {
                    updateField('description', event.target.value);
                  }}
                  readOnly={readOnly}
                  placeholder='Write a short description about the job'
                  isInvalid={
                    submitAttempted && Boolean(validationErrors.description)
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {validationErrors.description}
                </Form.Control.Feedback>
              </Form.Group>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setLocOpen(!locOpen);
            }}
            aria-controls='location-collapse'
            aria-expanded={locOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{locOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Location</Card.Title>
        </div>
        <Collapse in={locOpen}>
          <div id='location-collapse'>
            <Card.Body>
              {submitAttempted && validationErrors.location && (
                <Alert variant='danger' className='py-2'>
                  {validationErrors.location}
                </Alert>
              )}
              <Row>
                <Form.Group as={Col} controlId='locAddress' className='mb-3'>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.location?.address ?? ''}
                    onChange={event => {
                      updateLocation({address: event.target.value});
                    }}
                    readOnly={readOnly}
                    placeholder='Use \\n for multiple lines'
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='locCity' className='mb-3'>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.location?.city ?? ''}
                    onChange={event => {
                      updateLocation({city: event.target.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>
              </Row>

              <Row>
                <Form.Group as={Col} controlId='locPostal' className='mb-3'>
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.location?.postalCode ?? ''}
                    onChange={event => {
                      updateLocation({postalCode: event.target.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='locRegion' className='mb-3'>
                  <Form.Label>Region</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.location?.region ?? ''}
                    onChange={event => {
                      updateLocation({region: event.target.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='locCountry' className='mb-3'>
                  <Form.Label>Country Code</Form.Label>
                  <Form.Control
                    type='text'
                    value={form.location?.countryCode ?? ''}
                    onChange={event => {
                      updateLocation({countryCode: event.target.value});
                    }}
                    readOnly={readOnly}
                    placeholder='ISO-3166-1 ALPHA-2 (e.g. US)'
                  />
                </Form.Group>
              </Row>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setRespOpen(!respOpen);
            }}
            aria-controls='responsibilities-collapse'
            aria-expanded={respOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{respOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Responsibilities</Card.Title>
        </div>
        <Collapse in={respOpen}>
          <div id='responsibilities-collapse'>
            <Card.Body>
              {submitAttempted && validationErrors.responsibilities && (
                <Alert variant='danger' className='py-2'>
                  {validationErrors.responsibilities}
                </Alert>
              )}
              {safeArray(form.responsibilities).map((r, idx) => (
                <InputGroup className='mb-2' key={`resp-${idx}`}>
                  <Form.Control
                    value={r ?? ''}
                    onChange={event => {
                      updateStringArrayItem(
                        'responsibilities',
                        idx,
                        event.target.value,
                      );
                    }}
                    readOnly={readOnly}
                    placeholder='Describe responsibility'
                  />
                  {!readOnly && (
                    <>
                      <Button
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveStringArrayItem('responsibilities', idx, 'up');
                        }}
                        className='ms-2'
                      >
                        ↑
                      </Button>
                      <Button
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={
                          idx === safeArray(form.responsibilities).length - 1
                        }
                        onClick={() => {
                          moveStringArrayItem('responsibilities', idx, 'down');
                        }}
                        className='ms-1'
                      >
                        ↓
                      </Button>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeStringArrayItem('responsibilities', idx);
                        }}
                        className='ms-1'
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </InputGroup>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addStringArrayItem('responsibilities');
                  }}
                  className='mt-2'
                >
                  Add Responsibility
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setQualOpen(!qualOpen);
            }}
            aria-controls='qualifications-collapse'
            aria-expanded={qualOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{qualOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Qualifications</Card.Title>
        </div>
        <Collapse in={qualOpen}>
          <div id='qualifications-collapse'>
            <Card.Body>
              {submitAttempted && validationErrors.qualifications && (
                <Alert variant='danger' className='py-2'>
                  {validationErrors.qualifications}
                </Alert>
              )}
              {safeArray(form.qualifications).map((q, idx) => (
                <InputGroup className='mb-2' key={`qual-${idx}`}>
                  <Form.Control
                    value={q ?? ''}
                    onChange={event => {
                      updateStringArrayItem(
                        'qualifications',
                        idx,
                        event.target.value,
                      );
                    }}
                    readOnly={readOnly}
                    placeholder='List a qualification'
                  />
                  {!readOnly && (
                    <>
                      <Button
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveStringArrayItem('qualifications', idx, 'up');
                        }}
                        className='ms-2'
                      >
                        ↑
                      </Button>
                      <Button
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={
                          idx === safeArray(form.qualifications).length - 1
                        }
                        onClick={() => {
                          moveStringArrayItem('qualifications', idx, 'down');
                        }}
                        className='ms-1'
                      >
                        ↓
                      </Button>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeStringArrayItem('qualifications', idx);
                        }}
                        className='ms-1'
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </InputGroup>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addStringArrayItem('qualifications');
                  }}
                  className='mt-2'
                >
                  Add Qualification
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setSkillsOpen(!skillsOpen);
            }}
            aria-controls='skills-collapse'
            aria-expanded={skillsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{skillsOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Skills</Card.Title>
        </div>
        <Collapse in={skillsOpen}>
          <div id='skills-collapse'>
            <Card.Body>
              {submitAttempted && validationErrors.skills && (
                <Alert variant='danger' className='py-2'>
                  {validationErrors.skills}
                </Alert>
              )}
              {safeArray(form.skills).map((s, idx) => (
                <div key={`skill-${idx}`} className='mb-3 p-2 border rounded'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveSkill(idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.skills).length - 1}
                        onClick={() => {
                          moveSkill(idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`skillName-${idx}`}>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={s?.name ?? ''}
                        onChange={event => {
                          updateSkill(idx, {name: event.target.value});
                        }}
                        readOnly={readOnly}
                        placeholder='e.g. Web Development'
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`skillLevel-${idx}`}>
                      <Form.Label>Level</Form.Label>
                      <Form.Control
                        type='text'
                        value={s?.level ?? ''}
                        onChange={event => {
                          updateSkill(idx, {level: event.target.value});
                        }}
                        readOnly={readOnly}
                        placeholder='e.g. Master'
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group controlId={`skillKeywords-${idx}`}>
                    <Form.Label>Keywords (comma separated)</Form.Label>
                    <Form.Control
                      value={
                        keywordDrafts[idx] ?? (s?.keywords ?? []).join(', ')
                      }
                      onChange={event => {
                        const value_ = event.currentTarget.value;
                        setKeywordDrafts(previous => ({
                          ...previous,
                          [idx]: value_,
                        }));
                      }}
                      onBlur={event => {
                        const value_ = event.currentTarget.value;
                        const keywords = value_
                          .split(',')
                          .map(k => k.trim())
                          .filter(Boolean);
                        updateSkill(idx, {keywords});
                        setKeywordDrafts(previous => ({
                          ...previous,
                          [idx]: keywords.join(', '),
                        }));
                      }}
                      readOnly={readOnly}
                      placeholder='javascript, react, typescript'
                    />
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeSkill(idx);
                        }}
                      >
                        Remove Skill
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {!readOnly && (
                <Button onClick={addSkill} className='mt-2'>
                  Add Skill
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* meta is intentionally omitted from the UI */}

      <div className='d-flex justify-content-end'>{actionButtons}</div>
    </Form>
  );
}
